import { useMemo } from 'react';

import {
  AspectRatioStory,
  ClipOrder,
  ClipStartTimeAlignment,
  ClipTransitionAlignment,
  SelectionMethod,
  StoryConfig,
} from 'services/story.service';
import { useAppSelector } from 'store/store-hooks';
import { withSpaces } from 'components/app/_helpers/_app.helper';

import { ReactComponent as InfoIcon } from 'assets/ui/info.svg';
import { Tooltip } from 'components/UI/tooltip/tooltip';
import styles from './_finalcuts-header-info.module.scss';

// eslint-disable-next-line
const ENUMS: Partial<Record<keyof StoryConfig, (value: any) => string | number>> = {
  clipOrder: (val) => format(ClipOrder[val]),
  clipStartTimeAlignment: (val) => format(ClipStartTimeAlignment[val]),
  clipTransitionAlignment: (val) => format(ClipTransitionAlignment[val]),
  selectionMethod: (val) => format(SelectionMethod[val]),
  titleText: (val) => val,
  creditsText: (val) => val,
  aspectRatio: (val) => {
    switch (val) {
      case AspectRatioStory.is16x9:
        return '16/9';
      case AspectRatioStory.is9x16:
        return '9/16';
      case AspectRatioStory.is1x1:
        return '1/1';
      default:
        return format(AspectRatioStory[val]);
    }
  },
};

export const FinalcutsHeaderInfo = () => {
  const { fcStoryBoard, name, fcStoryConfig } = useAppSelector((state) => state.fc__data);

  const entries = useMemo(() => {
    if (!fcStoryConfig) return [];
    return extractDisplayableEntries(fcStoryConfig!);
  }, [fcStoryConfig]);

  return (
    <div className={styles.FcHeaderInfo}>
      {name} (Clips: {fcStoryBoard.filter(Boolean).length}){' '}
      <Tooltip
        className={styles.FcHeaderInfo__wrapper}
        position="top"
        showTooltip
        tip={
          <div className={styles.FcHeaderInfo__content}>
            <h3>Story Configuration</h3>
            {entries.map(([key, value]) => (
              <p key={key}>
                {key}: {value}
              </p>
            ))}
          </div>
        }
      >
        <div className={styles.FcHeaderInfo__ico}>
          <InfoIcon />
        </div>
      </Tooltip>
    </div>
  );
};

/* H E L P E R S */

const extractDisplayableEntries = (config: StoryConfig): [string, string | number][] => {
  return Object.entries(config)
    .map(([key, rawValue]) => {
      const formatter = ENUMS[key as keyof StoryConfig];
      const value = formatter
        ? formatter(rawValue)
        : typeof rawValue === 'string'
        ? format(rawValue)
        : typeof rawValue === 'number'
        ? rawValue
        : null;
      return value !== null ? [format(key), value] : null;
    })
    .filter((entry): entry is [string, string | number] => entry !== null);
};

const format = (val: string) => {
  return withSpaces(val.charAt(0).toUpperCase() + val.slice(1));
};
